/**
 * education-doc-groups.ts — Wave 70 (บริการที่ 12: เอกสารการศึกษา/วุฒิการศึกษา)
 *
 * แหล่งอ้างอิงที่ตรวจสอบได้เท่านั้น:
 *   - กระทรวงศึกษาธิการ (moe.go.th) — การรับรองวุฒิการศึกษาระดับการศึกษาขั้นพื้นฐาน
 *     และสำนักงานคณะกรรมการส่งเสริมการศึกษาเอกชน/สพฐ. สำหรับเอกสาร ปพ.1 ปพ.7
 *   - กระทรวงการอุดมศึกษา วิทยาศาสตร์ วิจัยและนวัตกรรม (อว. — mhesi.go.th) — การรับรอง
 *     หลักสูตร/สถาบันอุดมศึกษาและการเทียบคุณวุฒิผู้สำเร็จการศึกษาจากต่างประเทศ
 *   - สำนักงาน ก.พ. (ocsc.go.th) — การรับรองคุณวุฒิเพื่อประโยชน์ในการบรรจุและแต่งตั้ง
 *     ข้าราชการพลเรือน (ระบบรับรองคุณวุฒิ)
 *   - กรมการกงสุล กระทรวงการต่างประเทศ (consular.mfa.go.th) — งานนิติกรณ์รับรองเอกสาร
 *     และรับรองคำแปลเอกสารการศึกษา
 *   - สภาทนายความในพระบรมราชูปถัมภ์ — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร
 *     (Notarial Services Attorney) สำหรับ certified true copy ที่มหาวิทยาลัยต่างประเทศเรียก
 *   - ENIC-NARIC (ยุโรป) / NACES member evaluators เช่น WES, ECE (สหรัฐฯ) — การประเมิน
 *     คุณวุฒิต่างประเทศ ซึ่งเป็นระเบียบของหน่วยงานปลายทาง ไม่ใช่ของราชการไทย
 *   - อนุสัญญากรุงเฮก ค.ศ. 1961 (Apostille) — สถานะของไทยจาก HCCH (src/data/apostille-status.ts)
 *
 * กติกาเนื้อหา: ไม่ระบุราคา ไม่รับประกันผล ไม่กำหนดระยะเวลาราชการเอง
 * เงื่อนไขที่ผันแปรตามสถาบัน/ประเทศปลายทาง ให้เขียนว่า "ผันแปร" พร้อมวิธีตรวจสอบจากต้นทาง
 */
import { APOSTILLE_STATUS } from "@/data/apostille-status";

export interface EducationDocGroup {
  slug: string;
  titleTh: string;
  titleEn: string;
  summaryTh: string;
  summaryEn: string;
  bodyTh: string[];
  bodyEn: string[];
  documentsTh: string[];
  documentsEn: string[];
  chainTh: string[];
  chainEn: string[];
  checklistTh: string[];
  checklistEn: string[];
  faqsTh: { q: string; a: string }[];
  faqsEn: { q: string; a: string }[];
}

export const EDU_APOSTILLE_TH =
  `สถานะกฎหมาย: ไทยยื่นภาคยานุวัติสารเข้าเป็นภาคีอนุสัญญากรุงเฮก ค.ศ. 1961 (Apostille) เมื่อ ` +
  `${APOSTILLE_STATUS.accessionDepositTh} และอนุสัญญาจะมีผลบังคับใช้กับประเทศไทยในวันที่ ` +
  `${APOSTILLE_STATUS.entryIntoForceTh} (ที่มา: HCCH) ก่อนถึงวันดังกล่าว ปริญญาบัตร ทรานสคริปต์ ` +
  `และหนังสือรับรองการศึกษาของไทยที่จะใช้ในต่างประเทศ ยังต้องผ่านการรับรองนิติกรณ์ที่กรมการกงสุล ` +
  `แล้วจึงนำไปรับรองต่อที่สถานทูตของประเทศปลายทางตามลำดับเดิม`;

export const EDU_APOSTILLE_EN =
  `Legal status: Thailand deposited its instrument of accession to the 1961 Hague Apostille Convention on ` +
  `${APOSTILLE_STATUS.accessionDepositEn}; it enters into force for Thailand on ${APOSTILLE_STATUS.entryIntoForceEn} ` +
  `(source: HCCH). Until that date, Thai degrees, transcripts and school certificates used abroad still pass through ` +
  `the Department of Consular Affairs and then the destination country's embassy in the traditional order.`;

export const EDU_AUTHORITY_TH =
  `หน่วยงานที่รับผิดชอบต่างกันตามระดับการศึกษา: การศึกษาขั้นพื้นฐานอยู่ในความดูแลของกระทรวงศึกษาธิการ ` +
  `ส่วนหลักสูตรและสถาบันระดับอุดมศึกษาอยู่ในความดูแลของกระทรวงการอุดมศึกษา วิทยาศาสตร์ วิจัยและนวัตกรรม (อว.) ` +
  `ขณะที่การรับรองคุณวุฒิเพื่อบรรจุเข้ารับราชการพลเรือนเป็นอำนาจของสำนักงาน ก.พ. ` +
  `การรับรองลายมือชื่อผู้ออกเอกสารและการรับรองคำแปลเพื่อใช้ต่างประเทศเป็นงานของกรมการกงสุล กระทรวงการต่างประเทศ`;

export const EDU_AUTHORITY_EN =
  `Responsibility is split by level: basic education sits with the Ministry of Education, while higher-education ` +
  `programmes and institutions sit with the Ministry of Higher Education, Science, Research and Innovation (MHESI). ` +
  `Recognition of qualifications for Thai civil-service appointment is handled by the Office of the Civil Service ` +
  `Commission (OCSC). Authentication of the issuing signature and certification of translations for overseas use is ` +
  `the work of the Department of Consular Affairs, Ministry of Foreign Affairs.`;

export const EDUCATION_DOC_GROUPS: EducationDocGroup[] = [
  {
    slug: "degree-transcript-for-overseas-study",
    titleTh: "ปริญญาบัตรและทรานสคริปต์ไทยเพื่อสมัครเรียนต่อต่างประเทศ",
    titleEn: "Thai degree and transcript for applying to study abroad",
    summaryTh:
      "ชุดเอกสารหลักของผู้สมัครเรียนต่อ: ปริญญาบัตร ใบรับรองสำเร็จการศึกษา และทรานสคริปต์ฉบับภาษาอังกฤษที่ออกโดยมหาวิทยาลัย นำไปรับรองนิติกรณ์ที่กรมการกงสุล และรับรองต่อที่สถานทูตปลายทางเมื่อสถาบันหรือหน่วยงานตรวจคนเข้าเมืองเรียก",
    summaryEn:
      "The core admissions set: degree certificate, certificate of graduation and English-language transcript issued by the university, legalised at the Department of Consular Affairs and, where the institution or immigration authority requires it, further certified by the destination embassy.",
    bodyTh: [
      "มหาวิทยาลัยไทยส่วนใหญ่ออกทรานสคริปต์และหนังสือรับรองสำเร็จการศึกษาเป็นภาษาอังกฤษได้จากสำนักทะเบียนของสถาบันเอง เอกสารชุดภาษาอังกฤษที่ออกโดยตรงจากสถาบันมักได้รับการยอมรับดีกว่าคำแปลภายนอก เพราะมีลายมือชื่อและตราประทับของนายทะเบียนอยู่บนตัวเอกสาร จึงควรเริ่มจากการขอชุดภาษาอังกฤษจากสถาบันก่อนเสมอ",
      "ปริญญาบัตรมักออกเป็นภาษาไทยฉบับเดียว กรณีนี้ต้องแปลเป็นภาษาอังกฤษและนำไปรับรองคำแปลที่กองสัญชาติและนิติกรณ์ กรมการกงสุล การรับรองของกรมการกงสุลเป็นการรับรองว่าลายมือชื่อและตราประทับบนเอกสารเป็นของผู้มีอำนาจจริง ไม่ใช่การรับรองคุณภาพของหลักสูตรหรือผลการเรียน",
      "สถาบันปลายทางบางแห่งไม่รับเอกสารจากมือผู้สมัคร แต่กำหนดให้มหาวิทยาลัยต้นทางส่งตรง (official transcript sent directly) หรือส่งผ่านระบบอิเล็กทรอนิกส์ของสถาบัน เงื่อนไขข้อนี้ผันแปรตามสถาบัน ควรอ่านหน้า admissions requirements ของหลักสูตรนั้นก่อนเริ่มทำนิติกรณ์ เพื่อไม่ให้เสียเวลารับรองเอกสารที่ปลายทางไม่ใช้",
      "ประเทศที่เป็นภาคีอนุสัญญา Apostille จะเปลี่ยนไปใช้ Apostille แทนการรับรองซ้ำที่สถานทูตเมื่ออนุสัญญามีผลกับไทยแล้ว ระหว่างนี้ยังต้องใช้ลำดับเดิม คือ กรมการกงสุลก่อน แล้วจึงสถานทูตปลายทาง",
      "ชื่อ-นามสกุลบนปริญญาบัตร ทรานสคริปต์ และหนังสือเดินทางต้องสะกดตรงกัน หากสะกดต่างกัน เช่น เปลี่ยนนามสกุลหลังสมรส ให้เตรียมหลักฐานการเปลี่ยนชื่อสกุลหรือทะเบียนสมรสพร้อมคำแปลรับรองแนบไปด้วย เพื่อให้ปลายทางเชื่อมโยงตัวบุคคลได้",
    ],
    bodyEn: [
      "Most Thai universities can issue English-language transcripts and graduation certificates directly from their own registrar. Documents issued in English by the institution itself are usually accepted more readily than an external translation, because the registrar's signature and seal appear on the document. Always start by requesting the English set from the university.",
      "Degree certificates are often issued only in Thai. In that case the certificate must be translated into English and the translation certified by the Legalisation Division of the Department of Consular Affairs. That certification confirms that the signature and seal on the document are genuine; it is not an assessment of the programme or of academic performance.",
      "Some receiving institutions will not accept documents handed in by the applicant and require an official transcript sent directly by the issuing university or through its secure electronic system. This varies by institution, so read the programme's admissions requirements before starting legalisation, to avoid legalising documents the receiver will not use.",
      "For countries party to the Apostille Convention, an apostille will replace the second embassy certification once the Convention is in force for Thailand. Until then the traditional order applies: Department of Consular Affairs first, destination embassy second.",
      "The spelling of your name on the degree, the transcript and the passport must match. Where it differs — for example a surname changed after marriage — attach the name-change record or marriage certificate with a certified translation so the receiving office can link the documents to one person.",
    ],
    documentsTh: [
      "ปริญญาบัตรฉบับจริง พร้อมสำเนา",
      "ทรานสคริปต์ฉบับภาษาอังกฤษที่ออกโดยสำนักทะเบียนของสถาบัน (ปิดผนึกหากปลายทางกำหนด)",
      "หนังสือรับรองสำเร็จการศึกษาภาษาอังกฤษ (ถ้ามี)",
      "หนังสือเดินทางที่ยังไม่หมดอายุ และบัตรประชาชน",
      "หลักฐานการเปลี่ยนชื่อ-สกุล หรือทะเบียนสมรส หากชื่อไม่ตรงกับหนังสือเดินทาง",
      "หนังสือมอบอำนาจ หากให้ผู้อื่นดำเนินการแทน",
    ],
    documentsEn: [
      "Original degree certificate plus a copy",
      "English-language transcript issued by the university registrar (sealed if the receiver requires it)",
      "English certificate of graduation, if available",
      "Valid passport and Thai ID card",
      "Name-change record or marriage certificate if the name differs from the passport",
      "Power of attorney if someone else will file on your behalf",
    ],
    chainTh: [
      "ขอชุดเอกสารภาษาอังกฤษจากสำนักทะเบียนของมหาวิทยาลัย",
      "แปลเอกสารที่มีเฉพาะภาษาไทย เช่น ปริญญาบัตร เป็นภาษาอังกฤษ",
      "ยื่นรับรองนิติกรณ์/รับรองคำแปลที่กรมการกงสุล กระทรวงการต่างประเทศ",
      "นำไปรับรองต่อที่สถานทูตหรือสถานกงสุลของประเทศปลายทาง เมื่อปลายทางกำหนด",
      "ส่งเอกสารตามช่องทางที่สถาบันปลายทางกำหนด (ยื่นเอง ส่งไปรษณีย์ หรือให้สถาบันส่งตรง)",
    ],
    chainEn: [
      "Request the English document set from the university registrar",
      "Translate Thai-only documents such as the degree certificate into English",
      "File for legalisation / translation certification at the Department of Consular Affairs",
      "Obtain further certification at the destination country's embassy or consulate where required",
      "Submit through the channel the receiving institution specifies (in person, by post, or sent directly by the university)",
    ],
    checklistTh: [
      "อ่าน admissions requirements ของหลักสูตรและจดว่าปลายทางรับเอกสารรูปแบบใด",
      "ตรวจว่าชื่อสะกดตรงกันทุกฉบับกับหนังสือเดินทาง",
      "ตรวจว่าทรานสคริปต์ระบุวันสำเร็จการศึกษาและระบบเกรดครบถ้วน",
      "เก็บสำเนาชุดที่ผ่านการรับรองไว้อย่างน้อยหนึ่งชุดเสมอ",
      "สอบถามสถาบันว่าต้องการฉบับปิดผนึกหรือไม่ ก่อนเปิดซอง",
    ],
    checklistEn: [
      "Read the programme's admissions requirements and note the accepted document format",
      "Check that the name matches the passport on every document",
      "Check that the transcript shows the graduation date and the grading scale",
      "Keep at least one copy of the fully legalised set",
      "Ask whether a sealed envelope is required before opening it",
    ],
    faqsTh: [
      {
        q: "ต้องแปลทรานสคริปต์เองไหม ถ้ามหาวิทยาลัยออกภาษาอังกฤษให้แล้ว",
        a: "โดยทั่วไปไม่ต้อง เพราะเอกสารภาษาอังกฤษที่ออกโดยสำนักทะเบียนของสถาบันถือเป็นต้นฉบับอยู่แล้ว สิ่งที่ต้องทำต่อคือการรับรองนิติกรณ์เมื่อปลายทางเรียก",
      },
      {
        q: "กรมการกงสุลรับรองว่าอะไร",
        a: "รับรองว่าลายมือชื่อและตราประทับบนเอกสารเป็นของผู้มีอำนาจลงนามจริง หรือรับรองความถูกต้องของคำแปล ไม่ได้เป็นการรับรองคุณภาพหลักสูตรหรือผลการเรียน",
      },
      {
        q: "ถ้าปลายทางขอ Apostille ตอนนี้ทำได้หรือยัง",
        a: `อนุสัญญา Apostille จะมีผลกับประเทศไทยในวันที่ ${APOSTILLE_STATUS.entryIntoForceTh} ตามข้อมูลของ HCCH ก่อนวันดังกล่าวยังต้องใช้ลำดับรับรองแบบเดิมคือกรมการกงสุลแล้วตามด้วยสถานทูตปลายทาง`,
      },
      {
        q: "มหาวิทยาลัยปิดหลักสูตรหรือควบรวมไปแล้ว ขอเอกสารได้จากที่ไหน",
        a: "ต้องติดต่อสถาบันที่รับโอนภารกิจทะเบียนประวัตินักศึกษา ซึ่งผันแปรตามกรณี หากไม่ทราบ ให้สอบถามหน่วยงานต้นสังกัดของสถาบันในระดับอุดมศึกษาเพื่อชี้จุดที่เก็บระเบียนต่อ",
      },
      {
        q: "เอกสารมีอายุการใช้งานไหม",
        a: "ตัวปริญญาบัตรและทรานสคริปต์ไม่มีวันหมดอายุ แต่สถาบันปลายทางหลายแห่งกำหนดว่าการรับรองต้องออกไม่เกินระยะเวลาหนึ่ง เงื่อนไขนี้ผันแปรตามสถาบัน ควรตรวจกับปลายทางก่อน",
      },
      {
        q: "ยื่นแทนกันได้ไหม",
        a: "ทำได้ในหลายขั้นตอนโดยใช้หนังสือมอบอำนาจพร้อมสำเนาบัตรของผู้มอบและผู้รับมอบ แต่บางสถาบันกำหนดให้เจ้าของเอกสารดำเนินการเอง จึงควรตรวจสอบเป็นรายกรณี",
      },
    ],
    faqsEn: [
      {
        q: "Do I need to translate the transcript if the university already issues it in English?",
        a: "Usually not. An English transcript issued by the registrar is an original document. What remains is legalisation, if the receiving party asks for it.",
      },
      {
        q: "What exactly does the Department of Consular Affairs certify?",
        a: "The Department of Consular Affairs certifies that the signature and seal on a document belong to an authorized signatory, or that a certified translation is accurate. This process verifies document authenticity rather than evaluating the academic quality of educational programs or individual student grades.",
      },
      {
        q: "Can I get an apostille now if the receiver asks for one?",
        a: `The Apostille Convention enters into force for Thailand on ${APOSTILLE_STATUS.entryIntoForceEn} according to HCCH. Before that date the traditional chain applies: Department of Consular Affairs, then the destination embassy.`,
      },
      {
        q: "My university closed or merged. Where do I get records?",
        a: "You must contact the institution that took over the student records, which varies case by case. If you cannot identify it, ask the relevant higher-education authority to point you to where the records are kept.",
      },
      {
        q: "Do these documents expire?",
        a: "Degrees and transcripts do not expire, but many receiving institutions require the legalisation to be recent. The window varies by institution, so confirm with the receiver first.",
      },
      {
        q: "Can someone file on my behalf?",
        a: "For many steps yes, with a power of attorney and ID copies of both parties. Some institutions still require the document holder to appear, so check case by case.",
      },
    ],
  },
  {
    slug: "foreign-degree-recognition-in-thailand",
    titleTh: "เทียบวุฒิ/รับรองคุณวุฒิที่จบจากต่างประเทศเพื่อใช้ในประเทศไทย",
    titleEn: "Recognition of a foreign qualification for use in Thailand",
    summaryTh:
      "ผู้ที่จบจากต่างประเทศและต้องการใช้วุฒิในไทย ต้องแยกให้ชัดว่าจะใช้เพื่อสมัครงานเอกชน ศึกษาต่อ หรือบรรจุเข้ารับราชการ เพราะหน่วยงานที่รับรองและเอกสารที่ต้องใช้ต่างกัน",
    summaryEn:
      "If you graduated abroad and need the qualification recognised in Thailand, first identify the purpose — private employment, further study, or civil-service appointment — because the authority and the paperwork differ for each.",
    bodyTh: [
      "คำว่า 'เทียบวุฒิ' ในภาษาพูดครอบคลุมหลายเรื่องที่กฎหมายแยกกัน การรับรองคุณวุฒิเพื่อประโยชน์ในการบรรจุและแต่งตั้งข้าราชการพลเรือนเป็นอำนาจของสำนักงาน ก.พ. ส่วนการพิจารณาว่าวุฒิต่างประเทศเทียบเท่าระดับใดสำหรับการศึกษาต่อในสถาบันอุดมศึกษาไทย เป็นดุลพินิจของสถาบันที่รับเข้าศึกษา ภายใต้กรอบของกระทรวงการอุดมศึกษาฯ",
      "สำหรับการสมัครงานภาคเอกชน นายจ้างส่วนใหญ่ต้องการเพียงสำเนาปริญญาบัตรและทรานสคริปต์พร้อมคำแปลภาษาไทยที่รับรองแล้ว ไม่จำเป็นต้องผ่านการรับรองคุณวุฒิของ ก.พ. เว้นแต่ตำแหน่งนั้นอ้างอิงคุณสมบัติตามระเบียบราชการหรือวิชาชีพที่มีสภาวิชาชีพกำกับ",
      "เอกสารต้นทางที่ออกในต่างประเทศต้องผ่านการรับรองจากหน่วยงานของประเทศนั้นก่อน หากประเทศนั้นเป็นภาคี Apostille ก็ใช้ Apostille แล้วนำมาแปลเป็นไทยและรับรองคำแปลที่กรมการกงสุล หากไม่ใช่ภาคี ต้องผ่านกระทรวงการต่างประเทศของประเทศนั้นและสถานทูตไทยในประเทศนั้นตามลำดับ",
      "วิชาชีพที่มีสภาวิชาชีพกำกับ เช่น แพทย์ ทันตแพทย์ เภสัชกร พยาบาล วิศวกร สถาปนิก และทนายความ มีเงื่อนไขการรับรองและการสอบขึ้นทะเบียนของตนเองต่างหาก การรับรองคุณวุฒิทั่วไปไม่ทำให้ได้สิทธิประกอบวิชาชีพโดยอัตโนมัติ",
      "ระยะเวลาพิจารณาและรายการเอกสารของแต่ละหน่วยงานผันแปร และปรับปรุงเป็นระยะ ควรตรวจประกาศล่าสุดจากเว็บไซต์ของหน่วยงานนั้นก่อนยื่นทุกครั้ง",
    ],
    bodyEn: [
      "The everyday phrase 'credential recognition' covers several legally distinct processes. Recognition of a qualification for appointment to the Thai civil service is decided by the Office of the Civil Service Commission. Whether a foreign qualification counts as equivalent for admission to a Thai university is decided by the admitting institution within the framework set by MHESI.",
      "For private-sector jobs, most employers only need copies of the degree and transcript with a certified Thai translation. Civil-service recognition is not required unless the position is defined by public-service rules or sits under a regulated profession.",
      "Documents issued abroad must first be authenticated in the issuing country. If that country is a party to the Apostille Convention, an apostille is used; the document is then translated into Thai and the translation certified by the Department of Consular Affairs. If it is not a party, the document passes through that country's foreign ministry and the Royal Thai Embassy there.",
      "Regulated professions — medicine, dentistry, pharmacy, nursing, engineering, architecture, law — run their own recognition and licensing examinations. General credential recognition does not by itself confer the right to practise.",
      "Processing conditions and document lists vary by authority and are revised from time to time. Check the authority's current announcement before every filing.",
    ],
    documentsTh: [
      "ปริญญาบัตรต่างประเทศฉบับจริง พร้อมการรับรองจากประเทศต้นทาง (Apostille หรือสายรับรองแบบเดิม)",
      "ทรานสคริปต์ฉบับจริงพร้อมคำอธิบายระบบเกรด",
      "คำแปลภาษาไทยที่ผ่านการรับรองคำแปลจากกรมการกงสุล",
      "หนังสือเดินทางและหลักฐานการเข้า-ออกประเทศช่วงศึกษา หากหน่วยงานเรียก",
      "หลักฐานสถานะการรับรองของสถาบันในประเทศนั้น",
      "แบบคำขอตามที่หน่วยงานผู้รับรองกำหนด",
    ],
    documentsEn: [
      "Original foreign degree with authentication from the issuing country (apostille or the traditional chain)",
      "Original transcript with an explanation of the grading system",
      "Thai translation certified by the Department of Consular Affairs",
      "Passport and travel records for the study period, if requested",
      "Evidence of the institution's accreditation status in its own country",
      "The application form prescribed by the recognising authority",
    ],
    chainTh: [
      "ระบุวัตถุประสงค์: ทำงานเอกชน ศึกษาต่อ หรือบรรจุราชการ",
      "รับรองเอกสารต้นทางในประเทศที่ออกเอกสาร",
      "แปลเป็นภาษาไทยและรับรองคำแปลที่กรมการกงสุล",
      "ยื่นต่อหน่วยงานที่มีอำนาจตามวัตถุประสงค์นั้น",
      "หากเป็นวิชาชีพควบคุม ดำเนินการขึ้นทะเบียนกับสภาวิชาชีพแยกต่างหาก",
    ],
    chainEn: [
      "Identify the purpose: private employment, further study, or civil-service appointment",
      "Authenticate the documents in the country that issued them",
      "Translate into Thai and certify the translation at the Department of Consular Affairs",
      "File with the authority competent for that purpose",
      "For regulated professions, apply separately to the professional council",
    ],
    checklistTh: [
      "ตรวจว่าสถาบันที่จบได้รับการรับรองในประเทศต้นทาง",
      "ตรวจว่าเอกสารต้นทางรับรองครบก่อนนำเข้าไทย",
      "เตรียมคำอธิบายระบบเกรดและระยะเวลาหลักสูตร",
      "แยกเรื่องรับรองคุณวุฒิออกจากใบอนุญาตประกอบวิชาชีพ",
      "ตรวจประกาศล่าสุดของหน่วยงานก่อนยื่นทุกครั้ง",
    ],
    checklistEn: [
      "Confirm the institution is accredited in its own country",
      "Complete authentication abroad before bringing documents to Thailand",
      "Prepare an explanation of the grading scale and programme length",
      "Keep credential recognition separate from professional licensing",
      "Re-check the authority's current announcement before filing",
    ],
    faqsTh: [
      {
        q: "จบจากต่างประเทศ ต้องให้ ก.พ. รับรองทุกกรณีไหม",
        a: "ไม่ทุกกรณี การรับรองคุณวุฒิของสำนักงาน ก.พ. เป็นเรื่องของการบรรจุและแต่งตั้งข้าราชการพลเรือน หากใช้สมัครงานเอกชนหรือศึกษาต่อ ให้ดูเงื่อนไขของนายจ้างหรือสถาบันที่รับเข้าศึกษาเป็นหลัก",
      },
      {
        q: "หลักสูตรออนไลน์จากมหาวิทยาลัยต่างประเทศใช้ได้ไหม",
        a: "ขึ้นกับหน่วยงานผู้พิจารณาและเงื่อนไขของหลักสูตรนั้น หลายหน่วยงานดูว่าสถาบันได้รับการรับรองในประเทศต้นทางหรือไม่ และรูปแบบการเรียนตรงตามที่หลักสูตรกำหนดหรือไม่ จึงต้องตรวจเป็นรายกรณี",
      },
      {
        q: "ต้องแปลเอกสารก่อนหรือรับรองก่อน",
        a: "รับรองที่ประเทศต้นทางให้เสร็จก่อน แล้วจึงแปลเป็นไทยและรับรองคำแปล เพราะตราประทับรับรองที่เพิ่มมาภายหลังจะไม่อยู่ในคำแปลหากแปลก่อน",
      },
      {
        q: "วุฒิต่างประเทศใช้สมัครสอบใบอนุญาตวิชาชีพในไทยได้เลยไหม",
        a: "ไม่ได้โดยอัตโนมัติ สภาวิชาชีพแต่ละแห่งมีเกณฑ์รับรองหลักสูตรและการสอบของตนเอง ต้องยื่นตามระเบียบของสภาวิชาชีพนั้นแยกจากการรับรองคุณวุฒิทั่วไป",
      },
      {
        q: "เอกสารเป็นภาษาที่ไม่ใช่อังกฤษ ต้องทำอย่างไร",
        a: "แปลเป็นภาษาไทยโดยตรงและนำไปรับรองคำแปล การแปลผ่านภาษาที่สามอาจทำให้คำศัพท์คลาดเคลื่อนและถูกทักท้วงได้",
      },
      {
        q: "ถ้าปริญญาบัตรสูญหาย",
        a: "ต้องขอหนังสือรับรองการสำเร็จการศึกษาหรือใบแทนจากสถาบันที่ออกเอกสาร แล้วนำเอกสารฉบับใหม่เข้าสู่สายรับรองตามปกติ",
      },
    ],
    faqsEn: [
      {
        q: "Does every foreign graduate need OCSC recognition?",
        a: "No. OCSC recognition concerns appointment to the Thai civil service. For private employment or further study, the employer's or the admitting institution's own criteria apply.",
      },
      {
        q: "Are online degrees from foreign universities accepted?",
        a: "Online degrees from foreign universities are accepted depending on the specific deciding authority and educational program. Decision-making bodies evaluate whether the foreign institution is accredited in its home country and whether the study delivery mode met program requirements, so each case is assessed individually.",
      },
      {
        q: "Should I translate first or authenticate first?",
        a: "Authenticate abroad first, then translate into Thai and certify the translation. Seals added later would not appear in a translation made earlier.",
      },
      {
        q: "Can a foreign degree be used directly to sit a Thai professional licensing exam?",
        a: "Not automatically. Each professional council sets its own programme-recognition and examination rules, and you must apply to that council separately.",
      },
      {
        q: "My documents are in a language other than English. What now?",
        a: "Documents in a language other than English should be translated directly into Thai and properly certified. Translating your official documents directly into Thai prevents terminology errors and administrative objections that frequently occur when relaying text through an intermediate third language.",
      },
      {
        q: "What if the degree certificate is lost?",
        a: "Request a certificate of graduation or a replacement from the issuing institution, then run the new document through the normal authentication chain.",
      },
    ],
  },
  {
    slug: "school-records-transfer",
    titleTh: "เอกสารระดับโรงเรียน: ปพ.1 ปพ.7 และการย้ายสถานศึกษาข้ามประเทศ",
    titleEn: "School records: ป.พ.1, ป.พ.7 and cross-border school transfers",
    summaryTh:
      "ครอบครัวที่ย้ายประเทศต้องใช้ระเบียนแสดงผลการเรียน (ปพ.1) และใบรับรองผลการศึกษา (ปพ.7) พร้อมคำแปลรับรอง โรงเรียนปลายทางมักขอทั้งผลการเรียนรายปีและหลักฐานระดับชั้นที่กำลังศึกษา",
    summaryEn:
      "Relocating families need the Thai school record (ป.พ.1) and certificate of study (ป.พ.7) with certified translations. Receiving schools normally ask for year-by-year results as well as proof of the grade level currently attended.",
    bodyTh: [
      "เอกสารระดับการศึกษาขั้นพื้นฐานของไทยใช้แบบพิมพ์ของกระทรวงศึกษาธิการ ปพ.1 คือระเบียนแสดงผลการเรียนที่รวมผลรายวิชาตลอดช่วงชั้น ส่วน ปพ.7 คือใบรับรองว่ากำลังศึกษาหรือสำเร็จการศึกษาในระดับใด โรงเรียนปลายทางในต่างประเทศมักต้องการทั้งสองฉบับ เพราะฉบับหนึ่งบอกผลการเรียน อีกฉบับบอกสถานะปัจจุบัน",
      "โรงเรียนต่างประเทศจำนวนมากประเมินการจัดชั้นเรียนจากอายุร่วมกับผลการเรียน ไม่ใช่จากชื่อระดับชั้นของไทยโดยตรง จึงควรแนบคำอธิบายโครงสร้างระบบการศึกษาไทย เช่น ประถมศึกษา 6 ปี มัธยมศึกษาตอนต้น 3 ปี และมัธยมศึกษาตอนปลาย 3 ปี เพื่อลดการตีความผิด",
      "การแปลเอกสารโรงเรียนต้องคงชื่อวิชาและระบบคะแนนไว้ตามต้นฉบับ ไม่ควรแปลงเกรดไทยเป็นเกรดของประเทศปลายทางเอง เพราะการเทียบเกรดเป็นดุลพินิจของโรงเรียนหรือหน่วยงานประเมินปลายทาง",
      "ในทางกลับกัน เด็กที่ย้ายกลับเข้าเรียนในไทยต้องนำผลการเรียนจากต่างประเทศมาแปลเป็นไทยและรับรองคำแปล แล้วให้สถานศึกษาที่รับเข้าเรียนพิจารณาเทียบระดับชั้นตามระเบียบของกระทรวงศึกษาธิการ",
      "หากโรงเรียนต้นทางปิดกิจการ ให้ติดต่อหน่วยงานต้นสังกัดในพื้นที่ซึ่งเก็บรักษาระเบียนต่อ เงื่อนไขและจุดติดต่อผันแปรตามประเภทของโรงเรียน ทั้งโรงเรียนรัฐและเอกชน",
    ],
    bodyEn: [
      "Thai basic-education documents use Ministry of Education forms. ป.พ.1 is the academic record covering subject results across a school stage; ป.พ.7 certifies current enrolment or completion of a level. Overseas schools usually want both: one shows results, the other shows current status.",
      "Many foreign schools decide placement from age combined with results rather than from the Thai grade name. Attaching a short explanation of the Thai structure — six years of primary, three of lower secondary, three of upper secondary — reduces misplacement.",
      "Translations must keep subject names and the original grading scale. Do not convert Thai grades into the destination country's scale yourself; grade conversion is for the receiving school or evaluation body to decide.",
      "In the other direction, a child returning to Thai school must have the foreign results translated into Thai with a certified translation, after which the admitting school assesses placement under Ministry of Education rules.",
      "If the original school has closed, contact the local authority that holds its records. The contact point varies by school type, both state and private.",
    ],
    documentsTh: [
      "ปพ.1 ระเบียนแสดงผลการเรียน (ฉบับจริงหรือฉบับที่โรงเรียนรับรองสำเนาถูกต้อง)",
      "ปพ.7 ใบรับรองการเป็นนักเรียนหรือสำเร็จการศึกษา",
      "สูติบัตรหรือบัตรประชาชนของนักเรียน",
      "หนังสือเดินทางของนักเรียนและผู้ปกครอง",
      "คำแปลภาษาอังกฤษของเอกสารทั้งชุด",
      "คำอธิบายโครงสร้างระบบการศึกษาไทยและระบบเกรด",
    ],
    documentsEn: [
      "ป.พ.1 academic record (original or a copy certified by the school)",
      "ป.พ.7 certificate of study or completion",
      "The student's birth certificate or ID card",
      "Passports of student and parents",
      "English translations of the whole set",
      "An explanation of the Thai education structure and grading scale",
    ],
    chainTh: [
      "ขอ ปพ.1 และ ปพ.7 จากฝ่ายทะเบียนของโรงเรียน",
      "แปลเป็นภาษาอังกฤษโดยคงชื่อวิชาและระบบคะแนนเดิม",
      "รับรองคำแปลที่กรมการกงสุล เมื่อโรงเรียนหรือหน่วยงานปลายทางเรียก",
      "รับรองต่อที่สถานทูตปลายทาง หากปลายทางกำหนด",
      "ยื่นต่อโรงเรียนปลายทางพร้อมเอกสารประจำตัวนักเรียน",
    ],
    chainEn: [
      "Request ป.พ.1 and ป.พ.7 from the school registrar",
      "Translate into English, keeping subject names and the grading scale",
      "Certify the translation at the Department of Consular Affairs where required",
      "Add destination-embassy certification if required",
      "Submit to the receiving school with the student's identity documents",
    ],
    checklistTh: [
      "ตรวจว่าผลการเรียนครบทุกปีที่โรงเรียนปลายทางขอ",
      "ตรวจว่าชื่อนักเรียนตรงกับหนังสือเดินทาง",
      "ไม่แปลงเกรดเอง ให้แนบคำอธิบายระบบเกรดแทน",
      "ถามโรงเรียนปลายทางว่าต้องรับรองระดับใด ก่อนเริ่มทำนิติกรณ์",
      "สำเนาเก็บไว้ก่อนส่งต้นฉบับออกนอกประเทศ",
    ],
    checklistEn: [
      "Check that every school year the receiver asks for is included",
      "Check the student's name matches the passport",
      "Do not convert grades; attach the grading-scale explanation instead",
      "Ask the receiving school what level of certification it needs before starting",
      "Keep copies before sending originals abroad",
    ],
    faqsTh: [
      {
        q: "โรงเรียนปลายทางขอ transcript แต่ไทยมี ปพ.1 ใช้แทนกันได้ไหม",
        a: "ได้ ปพ.1 ทำหน้าที่เป็นระเบียนแสดงผลการเรียนของระดับการศึกษาขั้นพื้นฐาน เมื่อแปลเป็นภาษาอังกฤษพร้อมคำอธิบายระบบเกรดแล้ว โรงเรียนปลายทางจะใช้ประเมินได้",
      },
      {
        q: "ต้องรับรองที่กรมการกงสุลทุกครั้งไหม",
        a: "ไม่เสมอไป โรงเรียนเอกชนบางแห่งรับคำแปลที่รับรองโดยผู้แปลก็พอ แต่หน่วยงานราชการและการขอวีซ่ามักต้องการการรับรองที่กรมการกงสุล จึงควรถามปลายทางก่อน",
      },
      {
        q: "เด็กกลับมาเรียนที่ไทย ใช้ผลการเรียนต่างประเทศอย่างไร",
        a: "แปลเป็นภาษาไทยและรับรองคำแปล จากนั้นสถานศึกษาที่รับเข้าเรียนจะพิจารณาเทียบระดับชั้นตามระเบียบของกระทรวงศึกษาธิการ",
      },
      {
        q: "ขอ ปพ. ย้อนหลังหลายปีได้ไหม",
        a: "โรงเรียนเก็บระเบียนผลการเรียนไว้และออกเอกสารรับรองย้อนหลังได้ ระยะเวลาดำเนินการผันแปรตามโรงเรียน จึงควรติดต่อฝ่ายทะเบียนล่วงหน้า",
      },
      {
        q: "จำเป็นต้องส่งต้นฉบับให้โรงเรียนปลายทางไหม",
        a: "ผันแปร บางแห่งรับสำเนาที่รับรองแล้ว บางแห่งขอดูต้นฉบับตอนลงทะเบียน ควรถามให้ชัดก่อนส่งเอกสารออกนอกประเทศ",
      },
      {
        q: "ชื่อโรงเรียนภาษาอังกฤษควรใช้แบบใด",
        a: "ใช้ชื่อภาษาอังกฤษที่โรงเรียนใช้เป็นทางการ หากไม่มี ให้ถอดเสียงแบบเดียวกันตลอดทั้งชุดเอกสารเพื่อไม่ให้ปลายทางเข้าใจว่าเป็นคนละโรงเรียน",
      },
    ],
    faqsEn: [
      {
        q: "The receiving school asks for a transcript. Does ป.พ.1 count?",
        a: "Yes. ป.พ.1 is the academic record for Thai basic education. Translated into English with a grading-scale explanation, receiving schools can assess it.",
      },
      {
        q: "Is consular certification always required?",
        a: "Not always. Some private schools accept a translator-certified translation, while government bodies and visa applications usually require Department of Consular Affairs certification. Ask the receiver first.",
      },
      {
        q: "How are foreign results used when a child returns to Thailand?",
        a: "Translate them into Thai with a certified translation; the admitting school then decides grade placement under Ministry of Education rules.",
      },
      {
        q: "Can records from several years back be reissued?",
        a: "Academic records from several years back can usually be reissued because educational institutions maintain archived student records. Former students can request replacement certificates directly from their original school, though processing times vary by institution, making early contact with the registrar essential.",
      },
      {
        q: "Must originals be sent to the receiving school?",
        a: "Sending original documents to a receiving school depends on the specific institution's admission requirements. While some educational institutions accept certified copies for processing, others mandate physical inspection of original documents during enrollment. Applicants should always confirm specific documentation policies before sending original papers abroad.",
      },
      {
        q: "Which English school name should be used?",
        a: "Use the school's own official English name. If there is none, transliterate consistently across the whole set so the receiver does not read it as two different schools.",
      },
    ],
  },
  {
    slug: "student-visa-document-set",
    titleTh: "ชุดเอกสารสำหรับยื่นวีซ่านักเรียนต่างประเทศ",
    titleEn: "Document set for a foreign student visa application",
    summaryTh:
      "นอกจากหนังสือตอบรับจากสถาบัน สถานทูตส่วนใหญ่ยังขอหลักฐานการเงิน ความสัมพันธ์กับผู้สนับสนุน และเอกสารการศึกษาเดิมพร้อมคำแปลรับรอง รายการที่แน่นอนผันแปรตามประเทศและประเภทวีซ่า",
    summaryEn:
      "Beyond the offer letter, most embassies also require financial evidence, proof of the relationship with a sponsor, and previous education documents with certified translations. The exact list varies by country and visa category.",
    bodyTh: [
      "เอกสารวีซ่านักเรียนมีสามกลุ่มหลัก กลุ่มแรกคือหลักฐานการรับเข้าศึกษา เช่น หนังสือตอบรับหรือเอกสารยืนยันการลงทะเบียนที่สถาบันออกให้ กลุ่มที่สองคือหลักฐานว่ามีเงินเพียงพอตลอดหลักสูตร กลุ่มที่สามคือประวัติการศึกษาและเอกสารประจำตัว รายการย่อยและรูปแบบเป็นระเบียบของสถานทูตแต่ละแห่ง จึงต้องยึดประกาศของสถานทูตนั้นเป็นหลักเสมอ",
      "หลักฐานการเงินมักเป็นจุดที่ตกบ่อยที่สุด สถานทูตหลายแห่งกำหนดว่าเงินต้องอยู่ในบัญชีมาแล้วช่วงเวลาหนึ่ง และต้องอธิบายที่มาของเงินก้อนใหญ่ที่เพิ่งเข้าบัญชีได้ หากผู้สนับสนุนเป็นบิดามารดา ต้องแนบหลักฐานความสัมพันธ์ เช่น สูติบัตรหรือทะเบียนบ้าน พร้อมคำแปลรับรอง",
      "เอกสารราชการไทยที่ใช้ประกอบ เช่น สูติบัตร ทะเบียนบ้าน ทะเบียนสมรสของผู้ปกครอง มักต้องแปลเป็นภาษาอังกฤษและผ่านการรับรองคำแปลที่กรมการกงสุลก่อน สถานทูตบางแห่งกำหนดเพิ่มว่าต้องรับรองที่สถานทูตของตนอีกชั้นหนึ่ง",
      "เอกสารบางประเภทที่ออกโดยเอกชน เช่น หนังสือรับรองการทำงานของผู้สนับสนุนหรือหนังสือยินยอมของผู้ปกครอง มักต้องให้ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารรับรองลายมือชื่อผู้ลงนาม ก่อนนำเข้าสายนิติกรณ์",
      "การตัดสินให้หรือไม่ให้วีซ่าเป็นดุลพินิจของหน่วยงานตรวจคนเข้าเมืองของประเทศปลายทางเสมอ ไม่มีผู้ให้บริการใดรับประกันผลได้ สิ่งที่ทำได้คือทำให้ชุดเอกสารครบ ตรงกัน และอธิบายที่มาได้",
    ],
    bodyEn: [
      "Student-visa paperwork falls into three blocks: proof of admission such as an offer or enrolment confirmation from the institution; proof of sufficient funds for the whole course; and previous education plus identity documents. The sub-items and formats are set by each embassy, so its published list always governs.",
      "Financial evidence is the most common failure point. Many embassies require funds to have been held for a minimum period and expect large recent deposits to be explained. Where parents are the sponsors, attach proof of relationship such as a birth certificate or house registration with certified translations.",
      "Supporting Thai official documents — birth certificate, house registration, parents' marriage certificate — usually need English translations certified by the Department of Consular Affairs. Some embassies then require a further certification of their own.",
      "Privately issued documents such as a sponsor's employment letter or a parental consent letter usually need a Notarial Services Attorney to certify the signatory before entering the legalisation chain.",
      "The decision to grant a visa always rests with the destination country's immigration authority. No provider can guarantee an outcome; what can be controlled is a complete, internally consistent, explainable file.",
    ],
    documentsTh: [
      "หนังสือตอบรับหรือหลักฐานการลงทะเบียนจากสถาบันปลายทาง",
      "หนังสือเดินทางที่มีอายุครอบคลุมระยะเวลาศึกษา",
      "เอกสารการศึกษาเดิมพร้อมคำแปลรับรอง",
      "หลักฐานการเงินของผู้สมัครหรือผู้สนับสนุน และหนังสือรับรองสถานะทางการเงินจากธนาคาร",
      "หลักฐานความสัมพันธ์กับผู้สนับสนุน เช่น สูติบัตร ทะเบียนบ้าน",
      "หนังสือยินยอมของผู้ปกครองพร้อมการรับรองลายมือชื่อ กรณีผู้สมัครยังไม่บรรลุนิติภาวะ",
    ],
    documentsEn: [
      "Offer letter or enrolment confirmation from the receiving institution",
      "Passport valid for the study period",
      "Previous education documents with certified translations",
      "Financial evidence for the applicant or sponsor, plus a bank certification letter",
      "Proof of relationship with the sponsor, such as a birth certificate or house registration",
      "Parental consent letter with signature certification if the applicant is a minor",
    ],
    chainTh: [
      "อ่านรายการเอกสารจากประกาศของสถานทูตหรือหน่วยงานตรวจคนเข้าเมืองปลายทาง",
      "รวบรวมเอกสารราชการไทยและเอกสารการศึกษา",
      "รับรองลายมือชื่อเอกสารเอกชนโดยทนายความผู้ทำคำรับรอง",
      "แปลและรับรองคำแปลที่กรมการกงสุล",
      "รับรองเพิ่มที่สถานทูตปลายทาง หากกำหนด แล้วยื่นตามช่องทางที่ระบุ",
    ],
    chainEn: [
      "Read the document list published by the destination embassy or immigration authority",
      "Collect Thai official documents and education records",
      "Have private documents signature-certified by a Notarial Services Attorney",
      "Translate and certify the translations at the Department of Consular Affairs",
      "Add embassy certification if required, then file through the specified channel",
    ],
    checklistTh: [
      "ตรวจว่าชื่อและวันเดือนปีเกิดตรงกันทุกฉบับ",
      "ตรวจว่าหลักฐานการเงินครอบคลุมระยะเวลาที่สถานทูตกำหนด",
      "เตรียมคำอธิบายที่มาของเงินก้อนใหญ่ที่เพิ่งเข้าบัญชี",
      "ตรวจอายุหนังสือเดินทางให้ครอบคลุมตลอดหลักสูตร",
      "สำเนาชุดที่ยื่นเก็บไว้ทั้งชุดสำหรับการสัมภาษณ์",
    ],
    checklistEn: [
      "Check the name and date of birth match on every document",
      "Check the funds cover the period the embassy specifies",
      "Prepare an explanation for any large recent deposit",
      "Check passport validity covers the whole course",
      "Keep a full copy of the submitted file for the interview",
    ],
    faqsTh: [
      {
        q: "ต้องแปลเอกสารการเงินด้วยไหม",
        a: "ผันแปรตามสถานทูต หลายแห่งรับหนังสือรับรองจากธนาคารที่ออกเป็นภาษาอังกฤษอยู่แล้ว แต่สมุดบัญชีภาษาไทยมักต้องแปล ควรยึดประกาศของสถานทูตนั้นเป็นหลัก",
      },
      {
        q: "หนังสือยินยอมของผู้ปกครองต้องรับรองที่ไหน",
        a: "โดยทั่วไปให้ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารรับรองลายมือชื่อก่อน แล้วจึงนำไปรับรองที่กรมการกงสุลและสถานทูตตามที่ปลายทางกำหนด",
      },
      {
        q: "ยื่นวีซ่าแล้วเปลี่ยนสถาบันได้ไหม",
        a: "เป็นเรื่องของกฎหมายตรวจคนเข้าเมืองของประเทศนั้น หลายประเทศกำหนดให้แจ้งหรือยื่นใหม่ ควรตรวจกับหน่วยงานปลายทางก่อนตัดสินใจ",
      },
      {
        q: "มีการรับประกันว่าจะได้วีซ่าไหม",
        a: "ไม่มี การอนุมัติเป็นดุลพินิจของหน่วยงานปลายทาง เราช่วยได้ในส่วนความครบถ้วนและความถูกต้องของเอกสารเท่านั้น",
      },
      {
        q: "เอกสารที่รับรองแล้วใช้ยื่นหลายประเทศได้ไหม",
        a: "การรับรองที่กรมการกงสุลใช้ร่วมกันได้ แต่การรับรองชั้นสถานทูตผูกกับประเทศนั้นโดยเฉพาะ หากยื่นหลายประเทศต้องเตรียมชุดแยก",
      },
      {
        q: "ควรเริ่มเตรียมเอกสารเมื่อไร",
        a: "เริ่มทันทีที่ได้หนังสือตอบรับ เพราะบางขั้นตอน เช่น การขอเอกสารจากสถาบันเดิมและการนัดหมายสถานทูต มีคิวที่ควบคุมเองไม่ได้",
      },
    ],
    faqsEn: [
      {
        q: "Do financial documents need translation?",
        a: "Financial documents may require official translation depending on the receiving embassy's published guidelines. Many embassies accept bank letters directly if issued in English, but standard Thai bank passbooks usually require translation. Applicants must always consult the embassy's published checklist for exact document formatting.",
      },
      {
        q: "Where is a parental consent letter certified?",
        a: "Normally a Notarial Services Attorney certifies the signature first, then the Department of Consular Affairs and the embassy certify it as required.",
      },
      {
        q: "Can I change institution after applying?",
        a: "Changing institutions after applying is governed by the immigration laws of the destination country. Many national authorities require formal notification or a new visa application before allowing an institutional transfer. Applicants should consult the relevant immigration department before making any final decisions.",
      },
      {
        q: "Is a visa guaranteed?",
        a: "No. Approval is at the discretion of the destination authority. We can only help make the file complete and accurate.",
      },
      {
        q: "Can one legalised set be used for several countries?",
        a: "One legalised document set cannot always be used across several countries because embassy certifications are country-specific. Although general consular certification can sometimes be reused, embassy-level legalisation applies solely to one target nation, meaning multiple international applications require separate legalised document sets.",
      },
      {
        q: "When should preparation start?",
        a: "As soon as the offer arrives; steps such as obtaining records from a former institution and booking an embassy appointment run on queues you cannot control.",
      },
    ],
  },
  {
    slug: "professional-license-and-training-certificates",
    titleTh: "ใบประกอบวิชาชีพ ใบรับรองการอบรม และใบรับรองประสบการณ์ทำงาน",
    titleEn: "Professional licences, training certificates and experience letters",
    summaryTh:
      "ผู้สมัครงานหรือขอใบอนุญาตในต่างประเทศมักต้องยื่นใบประกอบวิชาชีพ ใบรับรองการอบรม และหนังสือรับรองประสบการณ์จากนายจ้าง ซึ่งแต่ละประเภทเข้าสายรับรองคนละแบบตามว่าใครเป็นผู้ออกเอกสาร",
    summaryEn:
      "Applicants for overseas jobs or licences often submit professional licences, training certificates and employer experience letters. Each type enters a different certification path depending on who issued it.",
    bodyTh: [
      "หลักสำคัญคือแยกว่าเอกสารออกโดยหน่วยงานรัฐ สภาวิชาชีพ หรือเอกชน ใบประกอบวิชาชีพที่ออกโดยสภาวิชาชีพและใบรับรองที่ออกโดยส่วนราชการเข้าสายนิติกรณ์ที่กรมการกงสุลได้โดยตรง ส่วนเอกสารที่ออกโดยบริษัทเอกชน เช่น หนังสือรับรองประสบการณ์ทำงาน ต้องให้ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารรับรองลายมือชื่อผู้ลงนามก่อน",
      "หนังสือรับรองประสบการณ์ที่ใช้ได้ผลจริงควรระบุตำแหน่ง ช่วงเวลาที่ทำงานเป็นวันเดือนปี ลักษณะงานโดยย่อ และชื่อพร้อมตำแหน่งของผู้ลงนาม หน่วยงานปลายทางหลายแห่งปฏิเสธหนังสือที่ระบุเพียงว่าเคยทำงานโดยไม่มีช่วงเวลาที่ชัดเจน",
      "ใบรับรองการอบรมจากผู้จัดอบรมเอกชนมีน้ำหนักต่างจากใบรับรองที่ออกโดยหน่วยงานกำกับดูแล การระบุชื่อหลักสูตร จำนวนชั่วโมง และหน่วยงานที่รับรองหลักสูตรไว้ในตัวเอกสาร ช่วยให้ปลายทางประเมินได้โดยไม่ต้องสอบถามเพิ่ม",
      "คำแปลของเอกสารกลุ่มนี้ต้องใช้ศัพท์วิชาชีพให้ตรง โดยเฉพาะชื่อตำแหน่งและขอบเขตใบอนุญาต การแปลชื่อวิชาชีพให้กว้างหรือแคบกว่าต้นฉบับอาจถูกมองว่าแสดงคุณสมบัติเกินจริง",
      "การที่เอกสารผ่านนิติกรณ์ครบไม่ได้แปลว่าประเทศปลายทางจะรับรองสิทธิประกอบวิชาชีพ การรับรองเอกสารเป็นเรื่องความแท้ของลายมือชื่อ ส่วนสิทธิประกอบวิชาชีพเป็นอำนาจขององค์กรกำกับดูแลในประเทศนั้น",
    ],
    bodyEn: [
      "The key distinction is who issued the document: a government agency, a professional council, or a private company. Licences from professional councils and certificates from government agencies can go straight into the consular legalisation chain. Privately issued documents such as employer experience letters need a Notarial Services Attorney to certify the signatory first.",
      "An experience letter that actually works states the job title, the employment period with exact dates, a short description of duties, and the signatory's name and position. Many receivers reject letters that merely confirm employment without dates.",
      "A certificate from a private training provider carries different weight from one issued by a regulator. Stating the course name, contact hours and the accrediting body on the certificate itself lets the receiver assess it without follow-up.",
      "Translations here must use exact professional terminology, particularly job titles and the scope of a licence. Rendering a professional title more broadly or narrowly than the original can read as misrepresenting qualifications.",
      "Full legalisation does not mean the destination country recognises a right to practise. Legalisation concerns the authenticity of signatures; the right to practise is decided by that country's regulator.",
    ],
    documentsTh: [
      "ใบอนุญาตประกอบวิชาชีพฉบับจริงและสำเนา",
      "หนังสือรับรองประสบการณ์ทำงานจากนายจ้าง ระบุตำแหน่งและช่วงเวลา",
      "ใบรับรองการอบรมพร้อมรายละเอียดหลักสูตรและจำนวนชั่วโมง",
      "หนังสือรับรองการจดทะเบียนบริษัทของนายจ้าง กรณีปลายทางขอตรวจสอบผู้ออกเอกสาร",
      "หนังสือเดินทางและบัตรประชาชนของผู้ยื่น",
      "คำแปลภาษาอังกฤษหรือภาษาปลายทางของทั้งชุด",
    ],
    documentsEn: [
      "Original professional licence and a copy",
      "Employer experience letter stating title and dates",
      "Training certificates with course details and contact hours",
      "The employer's company registration document if the receiver verifies the issuer",
      "The applicant's passport and Thai ID card",
      "Translations into English or the destination language for the whole set",
    ],
    chainTh: [
      "แยกเอกสารเป็นกลุ่มราชการ/สภาวิชาชีพ และกลุ่มเอกชน",
      "เอกสารเอกชน: รับรองลายมือชื่อโดยทนายความผู้ทำคำรับรอง",
      "แปลเป็นภาษาปลายทางโดยใช้ศัพท์วิชาชีพที่ตรง",
      "รับรองคำแปลและนิติกรณ์ที่กรมการกงสุล",
      "รับรองต่อที่สถานทูตปลายทาง หรือยื่นต่อองค์กรกำกับดูแลตามระเบียบของประเทศนั้น",
    ],
    chainEn: [
      "Separate government/professional-council documents from private ones",
      "Private documents: signature certification by a Notarial Services Attorney",
      "Translate into the destination language using precise professional terminology",
      "Certify the translation and legalise at the Department of Consular Affairs",
      "Add embassy certification, or file with the destination regulator under its own rules",
    ],
    checklistTh: [
      "ตรวจว่าหนังสือรับรองประสบการณ์ระบุช่วงเวลาชัดเจน",
      "ตรวจว่าผู้ลงนามมีอำนาจลงนามแทนบริษัทจริง",
      "ตรวจว่าใบอนุญาตยังไม่หมดอายุ ณ วันยื่น",
      "ตรวจคำแปลชื่อตำแหน่งให้ตรงกับต้นฉบับ ไม่ขยายความ",
      "เก็บช่องทางติดต่อของผู้ออกเอกสารไว้ เผื่อปลายทางขอตรวจสอบ",
    ],
    checklistEn: [
      "Check the experience letter states exact dates",
      "Check the signatory is authorised to sign for the company",
      "Check the licence is still valid on the filing date",
      "Check translated job titles match the original without embellishment",
      "Keep the issuer's contact details in case the receiver verifies",
    ],
    faqsTh: [
      {
        q: "หนังสือรับรองจากบริษัทเอกชนรับรองที่กรมการกงสุลได้เลยไหม",
        a: "โดยทั่วไปไม่ได้โดยตรง เพราะกรมการกงสุลรับรองลายมือชื่อของผู้มีอำนาจตามที่มีตัวอย่างลายมือชื่ออยู่ เอกสารเอกชนจึงมักต้องผ่านการรับรองลายมือชื่อโดยทนายความผู้ทำคำรับรองก่อน",
      },
      {
        q: "ใบอนุญาตวิชาชีพไทยใช้ทำงานต่างประเทศได้เลยไหม",
        a: "ไม่ได้โดยอัตโนมัติ ต้องยื่นขอรับรองหรือสอบตามระเบียบขององค์กรกำกับดูแลในประเทศนั้น การรับรองเอกสารเป็นเพียงการยืนยันความแท้ของเอกสาร",
      },
      {
        q: "บริษัทเดิมปิดกิจการ ขอหนังสือรับรองประสบการณ์ไม่ได้",
        a: "ใช้หลักฐานอื่นประกอบได้ เช่น หนังสือรับรองการหักภาษี ณ ที่จ่าย เอกสารประกันสังคม หรือสัญญาจ้าง ปลายทางจะพิจารณาน้ำหนักหลักฐานเป็นรายกรณี",
      },
      {
        q: "ต้องแปลเป็นภาษาอังกฤษหรือภาษาของประเทศปลายทาง",
        a: "ยึดตามที่ปลายทางกำหนด บางประเทศรับเฉพาะภาษาราชการของตน บางประเทศรับภาษาอังกฤษ ควรตรวจก่อนเริ่มแปลเพื่อไม่ต้องทำซ้ำ",
      },
      {
        q: "ใบรับรองการอบรมออนไลน์ใช้ได้ไหม",
        a: "ผันแปรตามผู้รับ หากใบรับรองระบุผู้จัด หลักสูตร จำนวนชั่วโมง และมีช่องทางตรวจสอบ โอกาสได้รับการยอมรับจะสูงกว่าใบรับรองที่ไม่มีรายละเอียด",
      },
      {
        q: "เอกสารชุดนี้ต้องรับรองใหม่ทุกครั้งที่สมัครงานไหม",
        a: "ไม่จำเป็นเสมอไป แต่ผู้รับหลายรายกำหนดอายุของการรับรอง จึงควรตรวจเงื่อนไขของผู้รับแต่ละรายก่อนใช้ชุดเดิมซ้ำ",
      },
    ],
    faqsEn: [
      {
        q: "Can a private company letter be legalised directly at the consular department?",
        a: "Usually not. That department verifies signatures it holds specimens for, so private documents normally need a Notarial Services Attorney to certify the signature first.",
      },
      {
        q: "Does a Thai professional licence allow me to work abroad?",
        a: "A Thai professional licence does not automatically permit you to work in a foreign country. Document legalisation only confirms the authenticity of the licence, requiring professionals to apply for official recognition or complete board examinations under the destination country's regulatory rules.",
      },
      {
        q: "My former employer has closed. What replaces the experience letter?",
        a: "Supporting evidence such as withholding-tax certificates, social-security records or employment contracts can be used; the receiver weighs the evidence case by case.",
      },
      {
        q: "Translate into English or the destination language?",
        a: "Follow the receiver's requirement. Some countries accept only their official language; others accept English. Check before translating to avoid doing it twice.",
      },
      {
        q: "Are online training certificates accepted?",
        a: "Online training certificates are accepted depending on the specific evaluation criteria of the receiving organisation. Certificates that explicitly detail the training provider, course name, completed contact hours, and an official verification pathway are significantly more likely to be accepted by reviewing authorities.",
      },
      {
        q: "Must the set be re-legalised for every job application?",
        a: "Not always, but many receivers set a validity window on the certification, so check each receiver's rule before reusing an older set.",
      },
    ],
  },
  {
    slug: "certified-translation-of-education-documents",
    titleTh: "การแปลและรับรองคำแปลเอกสารการศึกษา",
    titleEn: "Certified translation of education documents",
    summaryTh:
      "คำแปลเอกสารการศึกษาต้องคงชื่อสถาบัน ชื่อหลักสูตร ระบบเกรด และวันที่ให้ตรงต้นฉบับ พร้อมระบุระดับการรับรองที่ปลายทางต้องการ ตั้งแต่คำรับรองของผู้แปล ไปจนถึงการรับรองคำแปลที่กรมการกงสุล",
    summaryEn:
      "Translations of education documents must preserve institution names, programme titles, grading scales and dates exactly, and must match the level of certification the receiver requires — from a translator's declaration to certification by the Department of Consular Affairs.",
    bodyTh: [
      "ผู้รับเอกสารแต่ละประเภทต้องการระดับการรับรองต่างกัน มหาวิทยาลัยบางแห่งรับคำแปลที่มีคำรับรองและลายมือชื่อของผู้แปลพร้อมข้อมูลติดต่อ ขณะที่หน่วยงานราชการ การขอวีซ่า และการยื่นต่อองค์กรกำกับวิชาชีพ มักกำหนดให้รับรองคำแปลที่กรมการกงสุล การถามระดับที่ต้องการก่อนเริ่มงานช่วยไม่ให้ต้องทำซ้ำ",
      "งานแปลเอกสารการศึกษาแตกต่างจากงานแปลทั่วไปตรงที่ต้องรักษาโครงสร้างของต้นฉบับ ตารางรายวิชา หน่วยกิต และเกรด ต้องเรียงตรงกับต้นฉบับเพื่อให้ผู้ตรวจเทียบทีละบรรทัดได้ ตราประทับ ลายมือชื่อ และข้อความบนตราต้องระบุไว้ในคำแปลด้วยว่ามีอยู่ที่ตำแหน่งใด",
      "ชื่อเฉพาะเป็นจุดที่ต้องระวังที่สุด ชื่อสถาบันควรใช้ชื่อภาษาอังกฤษที่สถาบันใช้เป็นทางการ ไม่ใช่คำแปลใหม่ ส่วนชื่อบุคคลต้องถอดเสียงให้ตรงกับหนังสือเดินทางทุกฉบับ ความไม่สอดคล้องแม้ตัวอักษรเดียวอาจทำให้ปลายทางต้องขอเอกสารเพิ่ม",
      "ไม่ควรแปลงเกรดหรือคำนวณ GPA ใหม่ในคำแปล ให้คงตัวเลขและระบบเดิมไว้และแนบคำอธิบายระบบเกรดตามที่สถาบันประกาศ การเทียบเกรดเป็นหน้าที่ของหน่วยงานประเมินปลายทาง เช่น หน่วยประเมินคุณวุฒิในประเทศนั้น",
      "หากต้นฉบับมีการแก้ไข ขูดลบ หรือเป็นสำเนาที่อ่านไม่ชัด ควรขอเอกสารฉบับใหม่จากสถาบันก่อน เพราะการรับรองคำแปลอ้างอิงกับเอกสารที่นำมาแสดง หากต้นฉบับมีข้อสงสัย ปลายทางมักปฏิเสธทั้งชุด",
    ],
    bodyEn: [
      "Different receivers require different levels of certification. Some universities accept a translation carrying the translator's declaration, signature and contact details, while government offices, visa applications and professional regulators usually require certification by the Department of Consular Affairs. Asking which level is needed before work starts avoids doing it twice.",
      "Translating education documents differs from general translation because the layout must be preserved. Course tables, credits and grades must follow the original order so a reviewer can compare line by line, and seals, signatures and stamp text must be noted at the position where they appear.",
      "Proper nouns need the most care. Use the institution's own official English name rather than a fresh translation, and transliterate personal names exactly as in the passport across every document. A single-character inconsistency can trigger a request for more evidence.",
      "Do not convert grades or recalculate a GPA in the translation. Keep the original figures and scale and attach the institution's published grading explanation. Grade conversion is the job of the receiving evaluation body.",
      "If the original has corrections, erasures, or is an unclear copy, request a fresh document from the institution first. Translation certification refers to the document presented; doubts about the original usually sink the whole file.",
    ],
    documentsTh: [
      "ต้นฉบับเอกสารการศึกษาที่ชัดเจน ไม่มีรอยแก้ไข",
      "หนังสือเดินทางเพื่อใช้อ้างอิงการสะกดชื่อ",
      "คำอธิบายระบบเกรดของสถาบัน หากมี",
      "ข้อกำหนดของผู้รับเอกสารเกี่ยวกับระดับการรับรอง",
      "เอกสารเปลี่ยนชื่อ-สกุล หากชื่อไม่ตรงกัน",
    ],
    documentsEn: [
      "Clear originals with no alterations",
      "Passport, as the reference for name spelling",
      "The institution's grading-scale explanation, if available",
      "The receiver's stated certification requirement",
      "Name-change records if names differ",
    ],
    chainTh: [
      "ยืนยันกับผู้รับว่าต้องการการรับรองระดับใด",
      "แปลโดยคงโครงสร้าง ชื่อเฉพาะ และระบบเกรดเดิม",
      "ตรวจทานเทียบต้นฉบับทีละบรรทัด",
      "ลงคำรับรองของผู้แปล หรือยื่นรับรองคำแปลที่กรมการกงสุล",
      "รับรองเพิ่มที่สถานทูตปลายทาง หากปลายทางกำหนด",
    ],
    chainEn: [
      "Confirm with the receiver which certification level is required",
      "Translate, preserving layout, proper nouns and the grading scale",
      "Proofread line by line against the original",
      "Add the translator's declaration, or file for certification at the Department of Consular Affairs",
      "Add destination-embassy certification if required",
    ],
    checklistTh: [
      "ชื่อบุคคลตรงกับหนังสือเดินทางทุกฉบับ",
      "ชื่อสถาบันใช้ชื่อทางการภาษาอังกฤษของสถาบัน",
      "ไม่แปลงเกรดหรือคำนวณ GPA ใหม่",
      "ระบุตำแหน่งตราประทับและลายมือชื่อไว้ในคำแปล",
      "ตรวจวันที่ทั้งแบบพุทธศักราชและคริสต์ศักราชให้ตรงกัน",
    ],
    checklistEn: [
      "Personal names match the passport on every document",
      "Institution names use the official English name",
      "No grade conversion or recalculated GPA",
      "Seals and signatures noted at their positions",
      "Buddhist-era and Gregorian dates reconciled",
    ],
    faqsTh: [
      {
        q: "คำแปลต้องทำโดยนักแปลที่ขึ้นทะเบียนหรือไม่",
        a: "ขึ้นกับผู้รับ บางหน่วยงานกำหนดคุณสมบัติผู้แปลไว้ชัดเจน บางแห่งดูเพียงว่าคำแปลผ่านการรับรองจากหน่วยงานที่กำหนดหรือไม่ ควรถามผู้รับก่อนเสมอ",
      },
      {
        q: "แปลจากสำเนาได้ไหม",
        a: "ทำได้ในบางกรณี แต่การรับรองคำแปลมักต้องแสดงต้นฉบับหรือสำเนาที่ผู้ออกเอกสารรับรองความถูกต้อง จึงควรเตรียมต้นฉบับไว้ด้วย",
      },
      {
        q: "วันที่เป็น พ.ศ. ต้องแปลงเป็น ค.ศ. ไหม",
        a: "ในคำแปลภาษาอังกฤษควรแสดงปีคริสต์ศักราชให้ชัดเจน โดยคงข้อมูลเดิมไว้ครบเพื่อให้ตรวจย้อนกับต้นฉบับได้",
      },
      {
        q: "ทำไมปลายทางถึงตีกลับคำแปล",
        a: "สาเหตุที่พบบ่อยคือชื่อสะกดไม่ตรงหนังสือเดินทาง ตกรายละเอียดตราประทับ แปลงเกรดเอง หรือระดับการรับรองไม่ตรงกับที่ผู้รับกำหนด",
      },
      {
        q: "ต้องรับรองที่สถานทูตด้วยไหม",
        a: "เฉพาะกรณีที่ประเทศปลายทางกำหนด หลายกรณีการรับรองคำแปลที่กรมการกงสุลก็เพียงพอ ควรตรวจกับผู้รับก่อนเพื่อไม่ทำขั้นตอนเกินจำเป็น",
      },
      {
        q: "เอกสารหลายฉบับควรแปลพร้อมกันไหม",
        a: "ควร เพราะจะได้ใช้คำศัพท์และการถอดเสียงชื่อแบบเดียวกันทั้งชุด ลดความเสี่ยงที่ปลายทางจะมองว่าเป็นข้อมูลคนละชุด",
      },
    ],
    faqsEn: [
      {
        q: "Must the translator be registered?",
        a: "Translator registration requirements depend on the specific rules enforced by the receiving authority. While some institutions demand designated translator qualifications or official registration, others only require a valid certification statement on the document. Applicants should always confirm translator requirements with the receiver beforehand.",
      },
      {
        q: "Can a copy be translated?",
        a: "Translating a document copy is permitted in certain situations, but formal translation certification usually requires the original document or an official copy certified by the original issuer. Applicants should always keep the original document available to ensure successful authentication and acceptance.",
      },
      {
        q: "Should Buddhist-era dates be converted?",
        a: "An English translation should show the Gregorian year clearly while retaining the original information so it can be traced back to the source.",
      },
      {
        q: "Why do receivers reject translations?",
        a: "Most often: names that do not match the passport, omitted seal details, self-converted grades, or a certification level below what the receiver requires.",
      },
      {
        q: "Is embassy certification also needed?",
        a: "Only where the destination country requires it. In many cases consular certification of the translation suffices; check with the receiver to avoid unnecessary steps.",
      },
      {
        q: "Should several documents be translated together?",
        a: "Yes — one terminology and transliteration standard across the set reduces the risk that the receiver reads them as belonging to different people.",
      },
    ],
  },
];
